import React, { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import moment from "moment";
import { isMobile } from "react-device-detect";
import { DeleteOutlined, MonitorOutlined } from "@ant-design/icons";
import { Layout, Collapse, Button, Row } from "antd";
import snakeCase from "lodash/snakeCase";
import xor from "lodash/xor";

import { useSearchContext } from "../contexts/searchcontext.ts";
import { useCardsContext } from "../contexts/cardcontext";
import Services from "../../services";
import Toaster from "../../services/toaster";
import { useGraphContext } from "../contexts/graphcontext";
import withSavedSearchQueryParams from "../hocs/withSavedSearchQueryParams";
import SaveSearchForm from "../search/SaveSearchForm";
import useGlobal from "../../store";
import OptionListFiltered from "../layouts/OptionListFiltered";
import CascadingPanel from "../layouts/CascadingPanel";
import Helpers from "../../services/helpers";
import { SellerBuyerOptions } from "../SellerBuyerOptions";
import { selectEntity } from "./cardfilterwizardmenu.assets";

import { SAVED_SEARCH_TYPES } from "../../globals";
import { QUERY_RANGE_DATES } from "../charts/constants";
import { usePopularChartContext } from "../contexts/popularchartcontext";

const { Panel } = Collapse;

const CardFilterWizardMenu = ({
  stringifiedSearch,
  searchFields,
  requiredCount,
  filterFields = [],
  cardId,
  queryRange,
  from,
  to,
  savedSearchType,
  collapseSider,
  isQueryRcOnly,
  ...props
}) => {
  const router = useRouter();
  const searchContext = useSearchContext();
  const cardContext = useCardsContext();
  const graphContext = useGraphContext();
  const popularChartContext = usePopularChartContext();
  const [globalState] = useGlobal();

  const [isPrefilled, setIsPrefilled] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [performedSearch, setPerformedSearch] = useState(false);
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false);
  const [isForCardInitial, setIsForCardInitial] = useState(false);
  const [performedInitialSearch, setPerformedInitialSearch] = useState(false);
  const [showSavedSearchForm, setShowSavedSearchForm] = useState(false);
  const [isWizardLoading, setIsWizardLoading] = useState(false);
  const [initialExcludedSellers, setInitialExcludedSellers] = useState(
    globalState.excludeSellersFromChartResults || [],
  );
  const [initialExcludedPlatforms, setInitialExcludedPlatforms] = useState(
    globalState.excludePlatformsFromChartResults || [],
  );
  const [initialFeedbackExclusion, setInitialFeedbackExclusion] = useState({
    excludeNoFeedback: globalState.excludeNoFeedback,
    excludeLowFeedback: globalState.excludeLowFeedback,
    excludeNoSellerFeedback: globalState.excludeNoSellerFeedback,
  });
  const [initialAuctionExclusions, setInitialAuctionExclusions] = useState({
    excludeSaleWithMakeOffer: globalState.excludeSaleWithMakeOffer,
    excludeAuctions: globalState.excludeAuctions,
    excludeBuyItNow: globalState.excludeBuyItNow,
  });

  const [rookieYears, setRookieYears] = useState([]);
  const [rcOnly, setRcOnly] = useState(false);

  useEffect(() => {
    const _fn = async () => {
      if (!cardId) {
        return setIsPrefilled(true);
      }

      setIsPrefilled(false);

      await prefillCard();
      await getFilters(null, cardId);

      setIsPrefilled(true);
    };

    searchContext.setSearchCardId(cardId);

    _fn();
  }, [cardId]);

  useEffect(() => {
    const _fn = async () => {
      await search(true);

      searchContext.setAutoFetch(false);
    };

    if (searchContext.autoFetch) {
      _fn();
    }
  }, [searchContext.autoFetch]);

  const getPlayers = useCallback(async () => {
    const allPlayers = await Services.getPlayers({ includeNotes: true, minimal: true });

    searchContext.setPlayers(
      allPlayers.map((player) => ({
        label: player.name,
        value: player.id.toString(),
        notes: player.notes,
      })),
    );

    searchContext.setPlayersOriginal(allPlayers);
  }, [searchContext.setPlayers, searchContext.setPlayersOriginal]);

  useEffect(() => {
    async function fetchData() {
      switch (savedSearchType) {
        case SAVED_SEARCH_TYPES.PLAYER_CHARTS: {
          await getPlayers();
          break;
        }
        case SAVED_SEARCH_TYPES.SET_AND_YEAR: {
          const [allSports] = await Promise.all([Services.getSports(), getPlayers()]);
          searchContext.setSports(allSports.map((sport) => ({ label: sport.name, value: sport.id.toString() })));
          break;
        }
        default:
          break;
      }

      setDataLoaded(true);
    }

    const _fn = async () => {
      if (!dataLoaded) {
        await fetchData()
          .then(() => {})
          .catch((err) => Helpers.handleApiError(err));
      }

      if (props.queryParams.start_date) {
        searchContext.setStartDate(moment(props.queryParams.start_date));
      }

      if (props.queryParams.end_date) {
        searchContext.setEndDate(moment(props.queryParams.end_date));
      }
    };

    // Always reset the search context on load
    // State was getting polluted when migrating from playercharts to playercharts
    if (!props.hasQueryParams && !cardId) {
      resetAll();
    }

    _fn();
  }, [getPlayers]);

  useEffect(() => {
    if (!isPrefilled) {
      return;
    }

    const _fn = async () => {
      setIsForCardInitial(false);

      if (stringifiedSearch) {
        await getAndSetSearchEntities(stringifiedSearch);

        if (isQueryRcOnly) {
          setRcOnly(true);
        }
      }

      popularChartContext.reset();

      await search();
      await getFilters();

      setPerformedInitialSearch(true);
      searchContext.setSearchDisabled(false);
      setShowSavedSearchForm(true);
    };

    if (searchContext.searchCardId || props.hasQueryParams) {
      if (isForCardInitial) {
        _fn();
      }
    }
  }, [
    searchContext.searchCardId,
    searchContext.playersSelected,
    searchContext.setsSelected,
    searchContext.yearsSelected,
    searchContext.gradesSelected,
    searchContext.variationsSelected,
    searchContext.sportsSelected,
    isPrefilled,
    isForCardInitial,
  ]);

  useEffect(() => {
    const _fn = async () => {
      const offset = 24 * 60 * 60 * 1000 * 90;
      const start = new Date();
      start.setTime(start.getTime() - offset);

      const startDate = moment(start);
      const endDate = moment(new Date());
      searchContext.setStartDate(startDate);
      searchContext.setEndDate(endDate);

      if (stringifiedSearch) {
        await getAndSetSearchEntities(stringifiedSearch);
      }

      setPerformedInitialSearch(true);
      setCurrentStepIndex(searchFields.length);
      setIsForCardInitial(true);
    };

    if (performedInitialSearch && (queryRange || from || to)) {
      if (queryRange) {
        if (QUERY_RANGE_DATES[queryRange]) {
          searchContext.setStartDate(QUERY_RANGE_DATES[queryRange].value);
          searchContext.setDateRangeLabel(QUERY_RANGE_DATES[queryRange].label);
        }
      } else {
        if (from) {
          const startDate = moment(from, "MM/DD/YYYY");
          searchContext.setStartDate(startDate);
        }
        if (to) {
          const endDate = moment(to, "MM/DD/YYYY");
          searchContext.setEndDate(endDate);
        }
        searchContext.setDateRangeLabel();
      }
    }

    if (props.hasQueryParams && !performedInitialSearch) {
      _fn();
    }
  }, [props.hasQueryParams, performedInitialSearch]);

  const prefillCard = async () => {
    if (!cardId || cardId.split(",").length > 1) {
      return;
    }

    const card = await Services.getCard(cardId, {
      excludeResults: true,
      includePlayerNotes: true,
    });

    if (!card || card.error) {
      return Toaster.WarningNotification("Card not found");
    }

    const format = ({ id, name, notes = null }) => ({
      label: name,
      value: id.toString(),
      ...(notes ? { notes } : {}),
    });

    setIsForCardInitial(true);

    const selectedPlayers = [format(card.player)];
    const selectedSets = [format({ id: card.set_id, name: Helpers.generateSetName(card.card_set) })];
    const selectedYears = [format({ id: card.set_id, name: card.card_set.year })];
    const selectedGrades = [format(card.grade)];
    const selectedVariations = [
      format({
        ...card.variation,
        name: Helpers.generateSetVariationName({
          variation: card.variation,
          print_run: card.set_variation.print_run,
        }),
      }),
    ];
    const selectedSetNameYears = [
      format({
        name: Helpers.generateSetName(card.card_set),
        id: card.set_id,
      }),
    ];

    searchContext.setPlayersSelected(selectedPlayers);
    searchContext.setSetsSelected(selectedSets);
    searchContext.setYearsSelected(selectedYears);
    searchContext.setGradesSelected(selectedGrades);
    searchContext.setVariationsSelected(selectedVariations);
    searchContext.setSetNameYearsSelected(selectedSetNameYears);
    searchContext.setSearchDisabled(false);

    setCurrentStepIndex(searchFields.length);
  };

  useEffect(() => {
    const { setNameYearsOriginal, setNameYearsSelected, setSetNameYearsAvailable, setSetNameYearsSelected } =
      searchContext;

    if (rcOnly) {
      const filtered = setNameYearsOriginal.filter((set) => rookieYears.includes(set.year));
      const filteredSelected = setNameYearsSelected.filter((selected) =>
        filtered.some(({ value }) => value === selected.value),
      );
      setSetNameYearsAvailable(filtered);

      if (!isQueryRcOnly || hasSearchedOnce) {
        setSetNameYearsSelected(filteredSelected);
      }
    } else {
      setSetNameYearsAvailable(setNameYearsOriginal);
    }
  }, [rcOnly]);

  const resetAll = (resetSearch = true) => {
    if (resetSearch) {
      searchContext.reset();
    }
    graphContext.reset();
    popularChartContext.reset();
    cardContext.reset();
    searchContext.setSearchCardId(null);
    searchContext.setInitialSearchPerformed(false);
  };

  const gotToNextStep = async () => {
    setIsWizardLoading(true);

    const next = currentStepIndex < searchFields.length ? currentStepIndex + 1 : searchFields.length;
    const availableFilters = await getFilters();

    setCurrentStepIndex(next);

    if (next < requiredCount) {
      const nextFieldName = searchFields[next];
      const nextFilterName = snakeCase(nextFieldName);

      // If there's only one filter for the next filter, set it
      if (availableFilters && availableFilters[nextFilterName] && availableFilters[nextFilterName].length === 1) {
        const parsed = `${nextFieldName[0].toUpperCase()}${nextFieldName.slice(1)}`;
        searchContext[`set${parsed}Selected`](availableFilters[nextFilterName]);
      }
    }

    if (next >= requiredCount) {
      searchContext.setSearchDisabled(false);
    }

    setIsWizardLoading(false);
  };

  const goEdit = async (stepIndex) => {
    const next = stepIndex;
    setPerformedSearch(false);
    await getFilters(next);

    setCurrentStepIndex(next);
    if (next < requiredCount) {
      searchContext.setSearchDisabled(true);
    }

    resetAll(false);
  };

  const getFilters = async (nextStep, cardId) => {
    if (cardId?.split(",").length > 1) {
      return;
    }

    try {
      const parentFields = searchFields.slice(0, nextStep ? nextStep : currentStepIndex + 1);
      const params = searchContext.getParamsToApi(parentFields, { cardId, isSetVariation: true, savedSearchType });

      const availableFilters = await Services.getCardFilters(params);
      if (availableFilters.error) {
        return Toaster.ErrorNotification("Failed to fetch filters");
      }

      const {
        setSetsAvailable,
        setYearsAvailable,
        setGradesAvailable,
        setSportsAvailable,
        setPlayersAvailable,
        setSetNameYearsAvailable,
        setVariationsAvailable,
        setSetVariationIdsGroupedByName,
        setPlayersSelected,
        setPlayersFiltered,
        setSetNameYearsOriginal,
      } = searchContext;

      const { filters, rookieYears } = availableFilters;

      setRookieYears(rookieYears);

      filters.set_names && setSetsAvailable(filters.set_names);
      filters.set_years && setYearsAvailable(filters.set_years.sort(Helpers.sortByFieldNameNumericReverse("label")));
      filters.grades && setGradesAvailable(filters.grades);
      filters.sports && setSportsAvailable(filters.sports);
      filters.players && setPlayersAvailable(filters.players);

      if (filters.set_name_years) {
        setSetNameYearsAvailable(filters.set_name_years);
        setSetNameYearsOriginal(filters.set_name_years);
      }

      const setVariationOptions = Helpers.generateSetVariationsOptions(filters.set_variations);
      setVariationsAvailable(setVariationOptions.uniq);
      setSetVariationIdsGroupedByName(setVariationOptions.grouped);

      if (cardId && !hasSearchedOnce) searchContext.setVariationsSelected(setVariationOptions.uniq);

      filterFields.forEach((filterField) => {
        if (filterField === "players") {
          setPlayersSelected(filters.players);
          setPlayersFiltered(filters.players);
        }
      });

      return filters;
    } catch (err) {
      return Helpers.handleApiError(err);
    }
  };

  const reset = async () => {
    setShowSavedSearchForm(false);
    setCurrentStepIndex(0);
    searchContext.setSearchDisabled(true);
    searchContext.setIsSearchRunning(false);
    setPerformedSearch(false);
    setRookieYears([]);
    setRcOnly(false);
    resetAll();
  };

  const getAndSetSearchEntities = async (stringifiedSearch) => {
    const entities = await Services.listEntitiesBySearchIds(stringifiedSearch);
    searchContext.setEntitiesFromSearch(entities);
  };

  const search = async (isFromClick = false) => {
    if (isMobile) {
      collapseSider(true);
    }

    if (searchContext.isSearchRunning) {
      return;
    }

    const cid = isFromClick ? false : cardId;
    if (cid && cid.split(",").length > 1) {
      return;
    }

    const {
      excludeSellersFromChartResults,
      excludePlatformsFromChartResults,
      excludeNoFeedback,
      excludeNoSellerFeedback,
      excludeLowFeedback,
      excludeSaleWithMakeOffer,
      excludeAuctions,
      excludeBuyItNow,
    } = globalState;

    const params = {
      ...searchContext.getParamsToApi(null, {
        savedSearchType,
        returnRatios: true,
        cardId: cid,
        isSetVariation: true,
        rcOnly: rcOnly || isQueryRcOnly,
      }),
      excludeSellersFromChartResults,
      excludePlatformsFromChartResults,
      excludeNoFeedback,
      excludeNoSellerFeedback,
      excludeLowFeedback,
      excludeSaleWithMakeOffer,
      excludeAuctions,
      excludeBuyItNow,
    };

    if (isFromClick) {
      // clear url in cause user applied the filters
      router.push({ pathname: router.pathname }, undefined, { shallow: true });

      const initialStartDate = performedInitialSearch
        ? searchContext.startDate
        : moment().subtract(90, "d").format("YYYY-MM-DD");
      const initialEndDate = performedInitialSearch ? searchContext.endDate : moment().format("YYYY-MM-DD");

      params.startDate = initialStartDate;
      params.endDate = initialEndDate;

      if (!performedInitialSearch) {
        searchContext.setStartDate(moment().subtract(90, "d").startOf("day"));
        searchContext.setEndDate(moment().endOf("day"));
      }
    }

    searchContext.setDateRangeSearched([
      moment(params.startDate, "YYYY-MM-DD").startOf("day"),
      moment(params.endDate, "YYYY-MM-DD").endOf("day"),
    ]);

    let matchingCards;
    try {
      searchContext.setIsSearchRunning(true);

      matchingCards = await Services.searchCardResults(params);
      popularChartContext.setPointsByDay(matchingCards.chartData);
      popularChartContext.setCards(matchingCards.cards);
      searchContext.setPopularCardStats(matchingCards.individualCardStats);
    } catch (err) {
      return Helpers.handleApiError(err);
    } finally {
      searchContext.setIsSearchRunning(false);
    }

    if (matchingCards.cards?.length) {
      setPerformedSearch(true);
      setShowSavedSearchForm(true);
      setHasSearchedOnce(true);
    } else {
      if (searchContext.searchCardId) {
        router.push({ pathname: router.pathname }, undefined, { shallow: true });

        searchContext.setAutoFetch(true);
      } else {
        Toaster.WarningNotification("Search returned no results", "Try changing your selections.");
      }
    }

    cardContext.setCards(Array.isArray(matchingCards?.cards) ? matchingCards.cards : []);
    cardContext.setRatios(Array.isArray(matchingCards?.ratios) ? matchingCards.ratios : []);
    searchContext.setInitialSearchPerformed(true);
    setPerformedInitialSearch(true);
  };

  useEffect(() => {
    if (!isPrefilled || !searchContext.initialSearchPerformed) {
      return;
    }

    const {
      excludeSellersFromChartResults,
      excludePlatformsFromChartResults,
      excludeNoFeedback,
      excludeNoSellerFeedback,
      excludeLowFeedback,
      excludeSaleWithMakeOffer,
      excludeAuctions,
      excludeBuyItNow,
    } = globalState;
    // Check if all elements are the same in the 2 arrays
    if (xor(excludeSellersFromChartResults, initialExcludedSellers).length) {
      setInitialExcludedSellers(excludeSellersFromChartResults);
      search();
    } else if (xor(excludePlatformsFromChartResults, initialExcludedPlatforms).length) {
      setInitialExcludedPlatforms(excludePlatformsFromChartResults);
      search();
    } else if (
      initialFeedbackExclusion.excludeNoFeedback !== excludeNoFeedback ||
      initialFeedbackExclusion.excludeLowFeedback !== excludeLowFeedback ||
      initialFeedbackExclusion.excludeNoSellerFeedback !== excludeNoSellerFeedback
    ) {
      setInitialFeedbackExclusion({ excludeNoFeedback, excludeLowFeedback, excludeNoSellerFeedback });
      search();
    } else if (
      excludeSaleWithMakeOffer !== initialAuctionExclusions.excludeSaleWithMakeOffer ||
      excludeAuctions !== initialAuctionExclusions.excludeAuctions ||
      excludeBuyItNow !== initialAuctionExclusions.excludeBuyItNow
    ) {
      setInitialAuctionExclusions({ excludeSaleWithMakeOffer, excludeAuctions, excludeBuyItNow });
      search();
    }
  }, [
    globalState.excludeSellersFromChartResults,
    globalState.excludePlatformsFromChartResults,
    globalState.excludeNoFeedback,
    globalState.excludeNoSellerFeedback,
    globalState.excludeLowFeedback,
    globalState.excludeSaleWithMakeOffer,
    globalState.excludeAuctions,
    globalState.excludeBuyItNow,
  ]);

  useEffect(() => {
    if (!isPrefilled || !searchContext.initialSearchPerformed) {
      return;
    }
    search();
  }, [searchContext.startDate?.unix(), searchContext.endDate?.unix()]);

  return (
    <Layout className={"card-filter-wizard"} style={{ padding: 12 }}>
      <Collapse
        defaultActiveKey={["Search"]}
        activeKey={[...filterFields, "Search"]}
        className="sidebar-filter-collapse"
      >
        {searchFields.map((field) => (
          <CascadingPanel
            savedSearchType={savedSearchType}
            searchFields={searchFields}
            requiredCount={requiredCount}
            key={field}
            field={field}
            dataChanged={(fieldName, value) => selectEntity({ fieldName, value, searchContext })}
            currentStepIndex={currentStepIndex}
            queryParams={props.queryParams}
            gotToNextStep={gotToNextStep}
            goEdit={goEdit}
            rcOnly={rcOnly}
            setRcOnly={setRcOnly}
            isWizardLoading={isWizardLoading}
          />
        ))}
        <div style={{ backgroundColor: "#e5ecf8", padding: "16px 16px 2px 16px" }}>
          <Button.Group style={{ display: "flex", width: "100%" }}>
            <Button
              style={{ flex: "1 1 100%" }}
              type="primary"
              onClick={() => search(true)}
              size="large"
              disabled={searchContext.searchDisabled}
            >
              <MonitorOutlined />
              Search
            </Button>
            <Button type="secondary" disabled={searchContext.searchDisabled} onClick={reset} size={"large"}>
              <DeleteOutlined />
            </Button>
          </Button.Group>

          {showSavedSearchForm && (
            <Row style={{ padding: ".25rem" }}>
              <SaveSearchForm
                getTerms={() => Helpers.getTermsSV(searchContext, { rcOnly })}
                type={savedSearchType}
                setShowSavedSearchForm={setShowSavedSearchForm}
              />
            </Row>
          )}
        </div>

        {performedSearch && <SellerBuyerOptions />}
        {performedSearch &&
          filterFields.map((filterField) => (
            <Panel key={filterField} header={filterField}>
              <OptionListFiltered
                heading={filterField}
                fieldName={filterField}
                selectionsChanged={(fieldName, value) => selectEntity({ fieldName, value, searchContext })}
                hideByDefault={false}
                hideSearch={true}
              />
            </Panel>
          ))}
      </Collapse>
    </Layout>
  );
};

const CardFilterWizardWithSavedSearchQueryParams = withSavedSearchQueryParams(CardFilterWizardMenu);

export default CardFilterWizardWithSavedSearchQueryParams;
